// WhatsApp template authoring + approval board for one Jalpi number (WABA).
//
// Templates are the ONLY way to open a conversation outside WhatsApp's 24-hour
// service window, and Meta must approve each one before it can be sent. This
// screen writes one and watches it go PENDING → APPROVED; actually sending an
// approved template happens per-lead in WhatsAppTemplatePicker.
//
// Two constraints shape the form, both from the Jalpi API (routes_jalpi.py):
//   • There is no edit and no delete endpoint — a submitted name is permanent.
//   • Only the BODY may carry {{n}} variables, because the send path fills body
//     parameters only. Headers and buttons are fixed text here on purpose.
import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { ArrowLeft, Plus, RefreshCw, Loader2, Info, X, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const CATEGORIES = [
  { value: "UTILITY", label: "Utility — appointment updates, reminders, receipts" },
  { value: "MARKETING", label: "Marketing — offers, promotions, re-engagement" },
  { value: "AUTHENTICATION", label: "Authentication — one-time passcodes" },
];

// Meta wants a language code per template; the same name can exist once per
// language. These are the ones that actually come up for our branches.
const LANGUAGES = [
  { value: "en", label: "English (en)" },
  { value: "en_US", label: "English US (en_US)" },
  { value: "en_GB", label: "English UK (en_GB)" },
  { value: "hi", label: "Hindi (hi)" },
  { value: "mr", label: "Marathi (mr)" },
  { value: "gu", label: "Gujarati (gu)" },
  { value: "bn", label: "Bengali (bn)" },
  { value: "ta", label: "Tamil (ta)" },
  { value: "te", label: "Telugu (te)" },
  { value: "kn", label: "Kannada (kn)" },
  { value: "ml", label: "Malayalam (ml)" },
  { value: "pa", label: "Punjabi (pa)" },
];

const HEADER_TYPES = [
  { value: "none", label: "No header" },
  { value: "text", label: "Text" },
  { value: "image", label: "Image" },
  { value: "video", label: "Video" },
  { value: "document", label: "Document (PDF)" },
];

const BUTTON_TYPES = [
  { value: "QUICK_REPLY", label: "Quick reply" },
  { value: "URL", label: "Visit website" },
  { value: "PHONE_NUMBER", label: "Call phone number" },
];

const STATUS_STYLE = {
  APPROVED: "bg-green-50 text-green-700 border-green-200",
  PENDING: "bg-amber-50 text-amber-700 border-amber-200",
  IN_APPEAL: "bg-amber-50 text-amber-700 border-amber-200",
  REJECTED: "bg-red-50 text-red-700 border-red-200",
  DISABLED: "bg-red-50 text-red-700 border-red-200",
  PAUSED: "bg-orange-50 text-orange-700 border-orange-200",
};

const PLACEHOLDER_RE = /\{\{\s*(\d+)\s*\}\}/g;

function placeholderCount(text) {
  return ((text || "").match(PLACEHOLDER_RE) || []).length;
}

function bodyOf(tpl) {
  return (tpl.components || []).find((c) => c.type === "BODY")?.text || "";
}

// ── Approval board ──────────────────────────────────────────────────────────
function TemplateList({
  accounts, accountId, onAccountChange, templates, loading, onRefresh, onNew, onBack,
}) {
  return (
    <>
      <div className="flex items-center gap-2 mb-4">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <DialogTitle className="font-display text-navy text-lg flex-1">
          WhatsApp Templates
        </DialogTitle>
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading} className="gap-1">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
        <Button onClick={onNew} disabled={!accountId}
                className="bg-navy text-white hover:bg-navy-hover gap-1">
          <Plus className="h-4 w-4" /> New Template
        </Button>
      </div>

      {accounts.length > 1 && (
        <div className="mb-3">
          <Label className="text-xs text-muted-foreground">WhatsApp number</Label>
          <Select value={accountId || ""} onValueChange={onAccountChange}>
            <SelectTrigger><SelectValue placeholder="Choose a number" /></SelectTrigger>
            <SelectContent>
              {accounts.map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  {a.title}{a.instance_number ? ` · ${a.instance_number}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* overflow-hidden on the outer keeps the rounded corners clipping the
          header fill; the scroll lives on the inner wrapper so a narrow dialog
          scrolls the table sideways instead of silently hiding CATEGORY, LANG
          and STATUS off its right edge. min-w keeps the columns readable rather
          than squashing the name into one character per line. */}
      <div className="rounded-lg border border-navy/10 overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full min-w-[46rem] text-sm">
          <thead className="bg-cream/60 text-muted-foreground">
            <tr className="text-left">
              <th className="px-4 py-2.5 font-medium">NAME</th>
              <th className="px-4 py-2.5 font-medium w-28">CATEGORY</th>
              <th className="px-4 py-2.5 font-medium w-20">LANG</th>
              <th className="px-4 py-2.5 font-medium w-24">STATUS</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
              </td></tr>
            ) : templates.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">
                No templates on this number yet. Click <strong>New Template</strong> to submit one.
              </td></tr>
            ) : (
              templates.map((t) => (
                <tr key={`${t.name}-${t.language}`} className="border-t border-navy/5 align-top">
                  <td className="px-4 py-2.5">
                    <div className="font-medium">{t.name}</div>
                    <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{bodyOf(t)}</p>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">{t.category || "—"}</td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">{t.language || "—"}</td>
                  <td className="px-4 py-2.5">
                    <Badge variant="outline"
                           className={`text-[10px] ${STATUS_STYLE[String(t.status || "").toUpperCase()] || ""}`}>
                      {t.status || "UNKNOWN"}
                    </Badge>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      <p className="text-xs text-muted-foreground mt-3 flex gap-1.5">
        <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
        <span>
          WhatsApp reviews each template — usually within minutes, sometimes up to a day.
          Refresh to see the status change. Only <strong>APPROVED</strong> templates appear
          in the send picker on a lead.
        </span>
      </p>
    </>
  );
}

// ── Authoring form ──────────────────────────────────────────────────────────
function TemplateForm({ accountId, accountTitle, onBack, onCreated }) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState("UTILITY");
  const [language, setLanguage] = useState("en");
  const [headerType, setHeaderType] = useState("none");
  const [headerText, setHeaderText] = useState("");
  const [headerMediaUrl, setHeaderMediaUrl] = useState("");
  const [body, setBody] = useState("");
  const [examples, setExamples] = useState([]);
  const [footer, setFooter] = useState("");
  const [buttons, setButtons] = useState([]);
  const [saving, setSaving] = useState(false);

  const paramCount = useMemo(() => placeholderCount(body), [body]);

  // Keep one sample box per {{n}} as the body is typed.
  useEffect(() => {
    setExamples((prev) => {
      if (prev.length === paramCount) return prev;
      const next = prev.slice(0, paramCount);
      while (next.length < paramCount) next.push("");
      return next;
    });
  }, [paramCount]);

  const addVariable = () => setBody((b) => `${b}{{${placeholderCount(b) + 1}}}`);

  const addButton = () => {
    if (buttons.length >= 3) return;
    setButtons((b) => [...b, { type: "QUICK_REPLY", text: "", url: "", phone_number: "" }]);
  };
  const setButton = (i, patch) =>
    setButtons((b) => b.map((btn, idx) => (idx === i ? { ...btn, ...patch } : btn)));
  const removeButton = (i) => setButtons((b) => b.filter((_, idx) => idx !== i));

  // What the message will look like on the handset, samples substituted in.
  const preview = useMemo(() => {
    let i = 0;
    return (body || "").replace(PLACEHOLDER_RE, () => examples[i++] || "…");
  }, [body, examples]);

  const submit = async () => {
    if (!name.trim()) { toast.error("Template name is required"); return; }
    if (!body.trim()) { toast.error("Template body is required"); return; }
    if (examples.some((e) => !e.trim())) {
      toast.error("Fill in a sample value for every variable"); return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/jalpi/templates", {
        account_id: accountId,
        name: name.trim(),
        category,
        language,
        header_type: headerType,
        header_text: headerType === "text" ? headerText : null,
        header_media_url: headerType !== "none" && headerType !== "text" ? headerMediaUrl : null,
        body: body.trim(),
        body_examples: examples,
        footer: footer.trim() || null,
        buttons: buttons.map((b) => ({
          type: b.type,
          text: b.text.trim(),
          url: b.type === "URL" ? b.url.trim() : null,
          phone_number: b.type === "PHONE_NUMBER" ? b.phone_number.trim() : null,
        })),
      });
      toast.success(`Submitted "${data.name}" — WhatsApp is reviewing it`);
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to submit template");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-2 mb-4">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <DialogTitle className="font-display text-navy text-lg">
          New WhatsApp Template{accountTitle ? ` · ${accountTitle}` : ""}
        </DialogTitle>
      </div>

      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 mb-4 flex gap-2">
        <Info className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
        <p className="text-xs text-amber-800">
          The name is <strong>permanent</strong> — WhatsApp has no edit or delete, so a
          change means submitting a new template under a new name. Templates are also
          per-language: submit the same name again in Hindi to get a Hindi version.
        </p>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-muted-foreground">Name *</Label>
            <Input
              value={name}
              // Meta's rule: lowercase letters, digits and underscores only.
              onChange={(e) => setName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"))}
              placeholder="appointment_reminder"
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Language</Label>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {LANGUAGES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label className="text-xs text-muted-foreground">Category</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-muted-foreground mt-1">
            Pick honestly — WhatsApp rejects a promotional message filed as Utility, and
            Marketing messages cost more and can be blocked by the user.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-muted-foreground">Header</Label>
            <Select value={headerType} onValueChange={setHeaderType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {HEADER_TYPES.map((h) => <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {headerType === "text" && (
            <div>
              <Label className="text-xs text-muted-foreground">Header text (max 60)</Label>
              <Input value={headerText} maxLength={60}
                     onChange={(e) => setHeaderText(e.target.value)}
                     placeholder="Appointment confirmed" />
            </div>
          )}
          {headerType !== "none" && headerType !== "text" && (
            <div>
              <Label className="text-xs text-muted-foreground">Sample file URL (public https)</Label>
              <Input value={headerMediaUrl} onChange={(e) => setHeaderMediaUrl(e.target.value)}
                     placeholder="https://crm.dermatrix.in/uploads/banner.png" />
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between">
            <Label className="text-xs text-muted-foreground">Body * (max 1024)</Label>
            <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={addVariable}>
              + Add variable
            </Button>
          </div>
          <Textarea
            value={body}
            maxLength={1024}
            rows={4}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Hi {{1}}, your appointment at {{2}} is confirmed for {{3}}."
          />
          <p className="text-[11px] text-muted-foreground mt-1">
            Use {"{{1}}, {{2}}"} … for the parts that change per patient. Whoever sends the
            template fills these in.
          </p>
        </div>

        {paramCount > 0 && (
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">
              Sample values — WhatsApp needs a realistic example of each variable to review it
            </Label>
            {examples.map((val, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground w-12 shrink-0">{`{{${i + 1}}}`}</span>
                <Input value={val}
                       onChange={(e) => setExamples((p) => p.map((v, idx) => (idx === i ? e.target.value : v)))}
                       placeholder={i === 0 ? "Ravi Sharma" : "e.g. Andheri clinic"} />
              </div>
            ))}
          </div>
        )}

        <div>
          <Label className="text-xs text-muted-foreground">Footer (optional, max 60)</Label>
          <Input value={footer} maxLength={60} onChange={(e) => setFooter(e.target.value)}
                 placeholder="Dermatrix Skin & Hair Clinic" />
        </div>

        <div>
          <div className="flex items-center justify-between">
            <Label className="text-xs text-muted-foreground">Buttons (optional, up to 3)</Label>
            <Button variant="ghost" size="sm" className="h-6 text-xs"
                    onClick={addButton} disabled={buttons.length >= 3}>
              + Add button
            </Button>
          </div>
          {buttons.length === 0 ? (
            <p className="text-[11px] text-muted-foreground mt-1">
              Quick replies come back as an incoming message in the WhatsApp inbox.
            </p>
          ) : (
            <div className="space-y-2 mt-2">
              {buttons.map((b, i) => (
                <div key={i} className="flex items-start gap-2">
                  <Select value={b.type} onValueChange={(v) => setButton(i, { type: v })}>
                    <SelectTrigger className="w-40 shrink-0"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {BUTTON_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input value={b.text} maxLength={25} placeholder="Button label"
                         onChange={(e) => setButton(i, { text: e.target.value })} />
                  {b.type === "URL" && (
                    <Input value={b.url} placeholder="https://dermatrix.in/book"
                           onChange={(e) => setButton(i, { url: e.target.value })} />
                  )}
                  {b.type === "PHONE_NUMBER" && (
                    <Input value={b.phone_number} placeholder="+919812345678"
                           onChange={(e) => setButton(i, { phone_number: e.target.value })} />
                  )}
                  <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0"
                          onClick={() => removeButton(i)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Handset preview — what the patient actually receives. */}
        {(body || headerText) && (
          <div>
            <Label className="text-xs text-muted-foreground">Preview</Label>
            <div className="rounded-lg bg-[#e5ddd5] p-3 mt-1">
              <div className="rounded-lg bg-white p-3 shadow-sm max-w-sm text-sm">
                {headerType === "text" && headerText && (
                  <div className="font-semibold mb-1">{headerText}</div>
                )}
                {headerType !== "none" && headerType !== "text" && (
                  <div className="rounded bg-navy/5 text-[11px] text-muted-foreground px-2 py-3 mb-2 text-center">
                    [{headerType}]
                  </div>
                )}
                <div className="whitespace-pre-wrap">{preview}</div>
                {footer && <div className="text-[11px] text-muted-foreground mt-2">{footer}</div>}
                {buttons.length > 0 && (
                  <div className="border-t border-navy/10 mt-2 pt-2 space-y-1">
                    {buttons.map((b, i) => (
                      <div key={i} className="text-center text-[13px] text-sky-600">
                        {b.text || "Button"}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onBack}>Cancel</Button>
          <Button onClick={submit} disabled={saving}
                  className="bg-navy text-white hover:bg-navy-hover gap-1">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {saving ? "Submitting…" : "Submit for approval"}
          </Button>
        </div>
      </div>
    </>
  );
}

// ── Container ───────────────────────────────────────────────────────────────
export default function JalpiTemplates({ accounts, onBack }) {
  const [accountId, setAccountId] = useState(accounts[0]?.id || "");
  const [view, setView] = useState("list");   // list | form
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!accountId) { setTemplates([]); return; }
    setLoading(true);
    try {
      // status=all so PENDING/REJECTED show here — the send picker asks for the
      // default (approved only), since only those can actually be sent.
      const { data } = await api.get("/jalpi/templates", {
        params: { account_id: accountId, status: "all" },
      });
      setTemplates(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load templates");
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => { if (view === "list") load(); }, [view, load]);

  if (view === "form") {
    return (
      <TemplateForm
        accountId={accountId}
        accountTitle={accounts.find((a) => a.id === accountId)?.title}
        onBack={() => setView("list")}
        onCreated={() => setView("list")}
      />
    );
  }
  return (
    <TemplateList
      accounts={accounts}
      accountId={accountId}
      onAccountChange={setAccountId}
      templates={templates}
      loading={loading}
      onRefresh={load}
      onNew={() => setView("form")}
      onBack={onBack}
    />
  );
}
